import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  AlertProps,
  DataStatus,
  Parameter,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, Heading, Show, Text, VStack} from 'platform/foundation';
import {Lightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  RecordAuditResponseBody,
  mergeRecordAuditTasks,
  useDeleteRecordAuditMutation,
  useGetRecordAuditQuery,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {
  EntityHeaderAction,
  EntityHeaderFlag,
  EntityHeader,
  Page,
  RECORD_AUDIT_STATE_COLOR_SCHEME,
  RecordAuditFillPanel,
  RecordAuditParticipation,
  RecordAuditScorableResult,
  SignRecordAuditModal,
  handleApiError,
  isRecordAuditPairResourceId,
  resolveRecordAuditCounts,
  useOptimisticAuditHeader,
  useRecordAuditLifecycle,
  useRecordAuditTeamPickers,
  useVehicleHeaderImage,
} from '@dms/shared';

import {ApiError, buildArray, parseDate, useNavigate} from 'shared/web';

import {useRecordAuditHeaderSnippets} from '../../components/headerSnippets/useRecordAuditHeaderSnippets';
import {RecordAuditPairRecordDialog} from '../../components/RecordAuditPairRecordDialog/RecordAuditPairRecordDialog';
import {RECORD_AUDIT_ICON} from '../../constants/RECORD_AUDIT_ICON';
import {RECORD_AUDIT_PICKER_DIALOG_SIZE} from '../../constants/RECORD_AUDIT_PICKER_DIALOG_SIZE';

// Tags are open to everyone for now (no dedicated record-audit tag permissions yet);
// the backend RECORD_AUDIT_TAG resource gates the actual API.
const OPEN_TAG_PERMISSIONS = {
  isReadable: true,
  isCreatable: true,
  isUpdatable: true,
  isDeletable: true,
  isAssignable: true,
  isUnassignable: true,
};

export function RecordAuditDetail() {
  const {id: auditId} = useParams<{id: string}>();

  const auditQuery = useGetRecordAuditQuery(isNotNil(auditId) ? {auditId} : skipToken);
  const audit = auditQuery.data;

  return (
    <>
      <Helmet title={i18n.t('module.recordAudit.title')} />
      <DataStatus isLoading={auditQuery.isLoading} isError={auditQuery.isError} minHeight={50}>
        <Show when={audit}>{(audit) => <RecordAuditDetailLoaded audit={audit} />}</Show>
      </DataStatus>
    </>
  );
}

interface RecordAuditDetailLoadedProps {
  audit: RecordAuditResponseBody;
}

function RecordAuditDetailLoaded(props: RecordAuditDetailLoadedProps) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();

  const [deleteAudit, deleteAuditQuery] = useDeleteRecordAuditMutation();

  // Optimistic header/team state machine (auditor, responsible person, participants, branch,
  // paired record): instant updates, reconcile on refetch, per-field revert on error.
  const header = useOptimisticAuditHeader(props.audit);
  // Close / reopen / export / sign-modal — shared with the embedded fill manager.
  const lifecycle = useRecordAuditLifecycle(props.audit);
  const pickers = useRecordAuditTeamPickers(header);

  const tasks = mergeRecordAuditTasks(props.audit);

  const liveResults: RecordAuditScorableResult[] = tasks.map((task) => ({
    weight: task.weight,
    value: task.value,
  }));

  const isEditable = props.audit.state === 'OPEN';
  const isBranchAndRecordEditable = isEditable && props.audit.canChangeBranchAndRecord;

  const handleDelete = () => {
    openDeleteDialog({
      onConfirm: () =>
        deleteAudit({auditId: props.audit.id})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => navigate(recordAuditRoutes.overview))
          .catch((error: ApiError) => handleApiError(error)),
    });
  };

  const openPairDialog = () => {
    openDialog(
      <RecordAuditPairRecordDialog
        initialResourceId={
          isRecordAuditPairResourceId(header.effectiveRecord?.resourceId)
            ? header.effectiveRecord.resourceId
            : undefined
        }
        onConfirm={header.pairRecord}
        onClear={header.clearPair}
      />,
      {
        title: i18n.t('page.recordAudit.pairing.dialogTitle'),
        size: RECORD_AUDIT_PICKER_DIALOG_SIZE,
      }
    );
  };

  const {snippets, vehicleId} = useRecordAuditHeaderSnippets({
    testIdPrefix: 'recordAuditDetail',
    record: header.effectiveRecord,
    isPairEditable: isBranchAndRecordEditable,
    onPair: openPairDialog,
    isRecordUpdating: header.isRecordUpdating,
    branchId: header.effectiveBranchId,
    branchName: props.audit.branch.name,
    isBranchEditable: isBranchAndRecordEditable,
    isBranchUpdating: header.isBranchUpdating,
    onSelectBranch: header.selectBranch,
  });

  const [
    coverLightboxControls,
    coverPhotoUrl,
    onCoverPhotoClick,
    vehicleImages,
    photoDownloadSizeDialog,
  ] = useVehicleHeaderImage(vehicleId);

  const actions = buildAuditActions({
    audit: props.audit,
    lifecycle,
    isDeleting: deleteAuditQuery.isLoading,
    onDelete: handleDelete,
  });

  const counts = resolveRecordAuditCounts(props.audit, liveResults);
  const failedCount = counts.bad;

  const scoreResultFlag: EntityHeaderFlag | undefined = isNotNil(counts.passed)
    ? {
        label: `${counts.passed ? i18n.t('page.recordAudit.detail.passed') : i18n.t('page.recordAudit.detail.failed')}${isNotNil(counts.percentage) ? ` ${counts.percentage}%` : ''}`,
        colorScheme: counts.passed ? 'green' : 'red',
        isSubtle: true,
      }
    : undefined;

  const category = props.audit.definitionSnapshot.category;
  const categoryFlag: EntityHeaderFlag | undefined = isNotNil(category)
    ? {label: category, colorScheme: 'neutral', isSubtle: true}
    : undefined;

  const flags = buildArray<EntityHeaderFlag>()
    .add({
      label: i18n.t(`entity.recordAudit.states.${props.audit.state}`),
      colorScheme: RECORD_AUDIT_STATE_COLOR_SCHEME[props.audit.state],
    })
    .whenNotNil(scoreResultFlag)
    .whenNotNil(categoryFlag);

  const parameters = buildArray<Parameter>()
    .add(props.audit.auditNumber)
    .add(
      `${i18n.t('general.labels.created')} ${formatDateTime('dateTimeShort', parseDate(props.audit.createdAt))}`
    )
    .add(
      `${i18n.t('general.label.updated')} ${formatDateTime('dateTimeShort', parseDate(props.audit.updatedAt))}`
    );

  const primaryParameter = isNotNil(counts.percentage) ? `${counts.percentage}%` : undefined;

  const alerts: AlertProps[] | undefined =
    failedCount > 0
      ? [
          {
            variant: 'warning',
            message: i18n.t('page.recordAudit.detail.failedTasksAlert', {count: failedCount}),
          },
        ]
      : undefined;

  return (
    <>
      <Page
        data-testid="recordAuditDetail-page"
        header={
          <>
            <EntityHeader
              data-testid="recordAuditDetail-header"
              recordId={props.audit.id}
              resourceId={EntityResourceIds.recordAudit}
              permissions={{tag: OPEN_TAG_PERMISSIONS}}
              title={
                props.audit.definitionSnapshot.name || i18n.t('page.recordAudit.detail.untitled')
              }
              image={isNotNil(coverPhotoUrl) ? coverPhotoUrl : undefined}
              icon={isNotNil(coverPhotoUrl) ? undefined : RECORD_AUDIT_ICON}
              onImageClick={isNotNil(coverPhotoUrl) ? onCoverPhotoClick : undefined}
              flags={flags}
              parameters={parameters}
              primaryParameter={primaryParameter}
              snippets={snippets}
              actions={actions}
            />
            <Show when={isNotNil(alerts)}>
              <Box
                paddingHorizontal={[3, 3, 4]}
                paddingBottom={4}
                backgroundColor="palettes.white.10.100"
              >
                <VStack spacing={2}>
                  {alerts?.map((alert, index) => (
                    <Alert
                      key={index} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                      {...alert}
                      data-testid={`recordAuditDetail-header-alert-[${index}]`}
                    />
                  ))}
                </VStack>
              </Box>
            </Show>
          </>
        }
      >
        <Box
          flex={1}
          minWidth={0}
          paddingHorizontal={[3, 3, 4]}
          paddingVertical={4}
          paddingBottom={20}
        >
          <Box width="100%" maxWidth={400}>
            <VStack spacing={6}>
              <RecordAuditParticipation
                data-testid="recordAuditDetail"
                auditorEmployeeId={header.effectiveAuditorId}
                responsiblePersonEmployeeId={header.effectiveResponsiblePersonId}
                participants={header.realParticipants}
                nameByEmployeeId={header.nameByEmployeeId}
                isEditable={isEditable}
                isAuditorUpdating={header.isAuditorUpdating}
                isResponsiblePersonUpdating={header.isResponsiblePersonUpdating}
                loadingParticipantCount={header.loadingParticipantCount}
                onAddParticipant={pickers.openAddParticipantDialog}
                onRemoveParticipant={header.removeParticipant}
                onEditAuditor={pickers.openAuditorPicker}
                onEditResponsiblePerson={pickers.openResponsiblePersonPicker}
              />
              <RecordAuditFillPanel
                audit={props.audit}
                isEditable={isEditable}
                header={
                  <VStack spacing={0}>
                    <Heading size={4}>{i18n.t('page.recordAudit.detail.audit')}</Heading>
                    <Text size="xSmall" color="secondary">
                      {props.audit.definitionSnapshot.name}
                    </Text>
                  </VStack>
                }
              />
            </VStack>
          </Box>
        </Box>
      </Page>
      <SignRecordAuditModal
        auditId={props.audit.id}
        auditorName={header.auditorName}
        responsiblePersonName={header.responsiblePersonName}
        isOpen={lifecycle.isSignModalOpen}
        onClose={lifecycle.closeSignModal}
      />
      <Lightbox
        data-testid="recordAuditDetail-coverLightbox"
        controls={coverLightboxControls}
        images={vehicleImages}
      />
      {photoDownloadSizeDialog}
    </>
  );
}

interface BuildAuditActionsArgs {
  audit: RecordAuditResponseBody;
  lifecycle: ReturnType<typeof useRecordAuditLifecycle>;
  isDeleting: boolean;
  onDelete: () => void;
}

const buildAuditActions = (args: BuildAuditActionsArgs): EntityHeaderAction[] =>
  match(args.audit.state)
    .with('OPEN', () =>
      buildArray<EntityHeaderAction>()
        .add({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.close'),
          onClick: args.lifecycle.handleClose,
          isLoading: args.lifecycle.isClosing,
        })
        .add({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.delete'),
          onClick: args.onDelete,
          isLoading: args.isDeleting,
        })
    )
    .with('CLOSED', () =>
      buildArray<EntityHeaderAction>()
        .add({
          type: 'button',
          variant: 'primary',
          title: i18n.t('page.recordAudit.actions.sign'),
          onClick: args.lifecycle.openSignModal,
        })
        .add({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('page.recordAudit.actions.reopen'),
          onClick: args.lifecycle.handleReopen,
          isLoading: args.lifecycle.isReopening,
        })
        .add({
          type: 'button',
          variant: 'dangerOutlined',
          title: i18n.t('general.actions.delete'),
          onClick: args.onDelete,
          isLoading: args.isDeleting,
        })
    )
    .with('SIGNED', () =>
      buildArray<EntityHeaderAction>().add({
        type: 'button',
        variant: 'primary',
        title: i18n.t('page.recordAudit.actions.exportPdf'),
        onClick: args.lifecycle.handleExportPdf,
        isLoading: args.lifecycle.isExportingPdf,
      })
    )
    .exhaustive();
